import { createApi } from '@reduxjs/toolkit/query/react'
import { sendNotification } from 'components'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { mrfEnvironmentApi } from 'services/environment'
import { sendExportNotification, sendImportNotification } from 'utils'
import {
  MutationResponse,
  MaterialListResponse,
  MaterialListRequest,
  AddMaterialRequest,
  EditMaterialRequest,
  GetMaterialResponse,
  GetMaterialRequest,
  SearchMaterialRequest,
  SearchMaterialResponse,
  BulkDeleteRequest,
  ExportResponse,
  ExportRequest,
  BulkImportResponse,
  BulkImportRequest,
  type ListMaterialResponse,
  type ListMaterialRequest,
  MaterialStatus,
} from './types'
import { inventoryApi } from './inventory'
import { reportsApi } from 'services/ReportManagement/reports'
import type { MaterialResponse, MaterialUntransformedResponse } from 'services/environment/types'
import { GenericResponse } from 'services/types'

export const materialApi = createApi({
  reducerPath: 'materialApi',
  tagTypes: ['Materials', 'MaterialDetails', 'MaterialsDropdown'],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/MRF/'}`,
  }),
  endpoints: (builder) => ({
    getMaterialList: builder.query<MaterialListResponse, MaterialListRequest>({
      query: ({ client_id, ...data }) => ({
        url: `clients/${client_id}/material/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['Materials'],
    }),
    getMaterial: builder.query<GetMaterialResponse, GetMaterialRequest>({
      query: ({ clientId, materialId }) => ({
        url: `clients/${clientId}/material/?material_id=${materialId}`,
        method: 'GET',
      }),
      providesTags: ['MaterialDetails'],
    }),
    addMaterial: builder.mutation<MutationResponse, AddMaterialRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/material/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Materials', 'MaterialsDropdown'],
      async onQueryStarted(_, { dispatch }) {
        try {
          dispatch(mrfEnvironmentApi.util.invalidateTags(['MrfEnvironment']))
          dispatch(inventoryApi.util.invalidateTags(['Inventory', 'InventoryDetails']))
          // `onSuccess` side-effect
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editMaterial: builder.mutation<MutationResponse, EditMaterialRequest>({
      query: ({ clientId, materialId, ...data }) => ({
        url: `clients/${clientId}/material/${materialId}/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Materials', 'MaterialDetails', 'MaterialsDropdown'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(mrfEnvironmentApi.util.invalidateTags(['MrfEnvironment']))
          dispatch(inventoryApi.util.invalidateTags(['Inventory', 'InventoryDetails']))

          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    searchMaterial: builder.mutation<SearchMaterialResponse, SearchMaterialRequest>({
      query: ({ clientId, search }) => ({
        url: `clients/${clientId}/material/search/?query_param=${search}`,
        method: 'GET',
      }),
    }),
    listMaterialsDropdown: builder.query<
      ListMaterialResponse<MaterialResponse & { status: MaterialStatus }>,
      ListMaterialRequest
    >({
      query: ({ clientId, ...params }) => ({
        url: `v3/clients/${clientId}/material/`,
        method: 'GET',
        params,
      }),
      transformResponse: (
        response: ListMaterialResponse<MaterialUntransformedResponse & { status: MaterialStatus }>,
      ) => {
        return {
          ...response,
          data: response.data.map(({ id, ...rest }) => ({
            value: id,
            label: rest.name,
            ...rest,
          })),
        }
      },
      providesTags: ['MaterialsDropdown'],
    }),
    deleteMaterial: builder.mutation<MutationResponse, BulkDeleteRequest<number, string>>({
      query: ({ clientId, deleteIdArray }) => ({
        url: `clients/${clientId}/material/bulk_delete/`,
        method: 'DELETE',
        data: { material_id_list: deleteIdArray },
        errorOverride: true,
      }),
      invalidatesTags: ['Materials', 'MaterialDetails', 'MaterialsDropdown'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(mrfEnvironmentApi.util.invalidateTags(['MrfEnvironment']))
          dispatch(inventoryApi.util.invalidateTags(['Inventory', 'InventoryDetails']))

          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
          sendNotification({
            type: 'error',
            toastContent: (err as any).error.data.failure_list?.[0]?.description,
          })
        }
      },
    }),
    exportMaterials: builder.mutation<ExportResponse, ExportRequest>({
      query: ({ clientId, ...data }) => ({
        url: `clients/${clientId}/material/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (err) {}
      },
    }),
    bulkImportMaterials: builder.mutation<BulkImportResponse, BulkImportRequest>({
      query: ({ clientId, file }) => ({
        url: `clients/${clientId}/material/bulk_upload/`,
        method: 'POST',
        data: file,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendImportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ImportReports']))
        } catch (err) {}
      },
    }),
    editMaterialAttachments: builder.mutation<
      MutationResponse,
      {
        clientId: number
        materialId: number
        body: FormData
      }
    >({
      query: ({ clientId, materialId, body }) => ({
        url: `clients/${clientId}/material/${materialId}/edit_attachments/`,
        method: 'POST',
        data: body,
        errorOverride: true,
      }),
      invalidatesTags: ['MaterialDetails', 'Materials', 'MaterialsDropdown'],
    }),
    updateMaterialStatus: builder.mutation<
      GenericResponse,
      { clientId: number; materialId: number; status: MaterialStatus }
    >({
      query: ({ clientId, materialId, status }) => ({
        url: `clients/${clientId}/material/${materialId}/status/`,
        method: 'PATCH',
        data: { status },
      }),
      invalidatesTags: ['MaterialDetails', 'Materials', 'MaterialsDropdown'],
    }),
  }),
})
